(() => {
  const rootSelector = '[data-selected-options-card-list]';
  const itemsSelector = '[data-selected-options-card-list-items]';
  const controlsSelector = '[data-selected-options-card-list-controls]';
  const updatedAnimationFallbackMs = 1700;

  if (document.documentElement.dataset.selectedOptionsCardListBound === 'true') return;
  document.documentElement.dataset.selectedOptionsCardListBound = 'true';

  function normalizeImage(image, fallbackAlt) {
    if (!image) return null;

    if (typeof image === 'string') {
      return image ? { url: image, alt: fallbackAlt || '' } : null;
    }

    const url = image.url || image.thumbnailUrl || image.src || image.previewUrl || '';
    if (!url) return null;

    return {
      url,
      alt: image.alt || image.altText || fallbackAlt || '',
    };
  }

  function getOptionImage(option, imageSource = 'preview') {
    const previewMedia = Array.isArray(option?.previewMedia) ? option.previewMedia : [];
    const previewCandidates = [
      previewMedia[0],
      option?.preview,
      option?.previewImage,
      option?.thumbnail,
      option?.thumbnailUrl,
      option?.image,
    ];
    const thumbnailCandidates = [
      option?.thumbnail,
      option?.thumbnailUrl,
      previewMedia[0],
      option?.preview,
      option?.previewImage,
      option?.image,
    ];
    const candidates = imageSource === 'thumbnail' ? thumbnailCandidates : previewCandidates;

    for (const candidate of candidates) {
      const image = normalizeImage(candidate, option?.label || 'Selected option');
      if (image) return image;
    }

    return null;
  }

  function getSelectedOptions(snapshot) {
    if (Array.isArray(snapshot?.selectedOptions)) return snapshot.selectedOptions;

    const config = snapshot?.config || {};
    const selections = snapshot?.selections || {};

    return (config.groups || []).flatMap((group) => {
      const rawSelection = Array.isArray(selections[group.groupTypeKey])
        ? selections[group.groupTypeKey]
        : (selections[group.groupTypeKey] ? [selections[group.groupTypeKey]] : []);

      return rawSelection.map((optionKey) => {
        const option = (group.options || []).find((entry) => String(entry.key) === String(optionKey));
        if (!option) return null;

        return {
          ...option,
          groupKey: group.groupTypeKey,
          groupLabel: group.label || group.groupTypeKey || '',
        };
      }).filter(Boolean);
    });
  }

  function getOptionKey(option) {
    return String(option?.key || option?.optionKey || option?.id || option?.label || '');
  }

  function getSelectionGroups(selections = {}) {
    return Object.entries(selections || {}).reduce((groups, [groupKey, values]) => {
      const normalizedValues = Array.isArray(values) ? values : (values ? [values] : []);
      groups[groupKey] = normalizedValues.map(String).sort();
      return groups;
    }, {});
  }

  function getChangedGroupKeys(previousSelections, nextSelections) {
    if (!previousSelections) return null;

    const previousGroups = getSelectionGroups(previousSelections);
    const nextGroups = getSelectionGroups(nextSelections);
    const groupKeys = new Set([...Object.keys(previousGroups), ...Object.keys(nextGroups)]);

    return Array.from(groupKeys).filter((groupKey) => {
      const previousValue = (previousGroups[groupKey] || []).join('\u0000');
      const nextValue = (nextGroups[groupKey] || []).join('\u0000');
      return previousValue !== nextValue;
    });
  }

  function setCommittedSelections(root, snapshot) {
    root.__selectedOptionsCommittedSelections = snapshot?.selections
      ? JSON.parse(JSON.stringify(snapshot.selections))
      : null;
  }

  function createOptionMedia(row) {
    const media = document.createElement('div');
    media.className = 'selected-options-card-list__media';

    if (row.image?.url) {
      const image = document.createElement('img');
      image.src = row.image.url;
      image.alt = row.image.alt || row.optionLabel || row.label || '';
      image.loading = 'lazy';
      media.appendChild(image);
      return media;
    }

    const placeholder = document.createElement('span');
    placeholder.className = 'selected-options-card-list__media-placeholder';
    placeholder.setAttribute('aria-hidden', 'true');
    media.appendChild(placeholder);
    return media;
  }

  function createOptionCard(option, imageSource, slideId) {
    const item = document.createElement('div');
    item.id = slideId;
    item.className = 'selected-options-card-list__item slider__slide';
    item.setAttribute('role', 'listitem');
    item.dataset.selectedOptionsCardGroupKey = String(option.groupKey || '');
    item.dataset.selectedOptionsCardOptionKey = getOptionKey(option);

    const row = {
      label: option.label || '',
      optionLabel: option.label || '',
      groupLabel: option.groupLabel || '',
      image: getOptionImage(option, imageSource),
    };

    const content = document.createElement('div');
    content.className = 'selected-options-card-list__content';

    const title = document.createElement('h4');
    title.className = 'selected-options-card-list__title truncate-text';
    title.textContent = row.optionLabel || row.label;

    const type = document.createElement('p');
    type.className = 'selected-options-card-list__type truncate-text';
    type.textContent = row.groupLabel;

    content.append(title, type);
    item.append(createOptionMedia(row), content);
    return item;
  }

  function animateSavedCards(root, changedGroupKeys) {
    const itemsRoot = root.querySelector(itemsSelector);
    if (!itemsRoot) return;

    const changedGroups = Array.isArray(changedGroupKeys) ? changedGroupKeys.map(String) : null;
    const cards = Array.from(itemsRoot.querySelectorAll('.selected-options-card-list__item'));
    const targetCards = changedGroups === null
      ? cards
      : cards.filter((card) => changedGroups.includes(card.dataset.selectedOptionsCardGroupKey || ''));

    targetCards.forEach((card) => {
      let cleanupTimeout;
      const cleanup = () => {
        window.clearTimeout(cleanupTimeout);
        card.removeEventListener('animationend', cleanup);
        card.classList.remove('is-updated');
      };

      card.classList.remove('is-updated');
      card.classList.add('is-updated');
      card.addEventListener('animationend', cleanup, { once: true });
      cleanupTimeout = window.setTimeout(cleanup, updatedAnimationFallbackMs);
    });
  }

  function getBuilder(root) {
    const builderId = root.dataset.selectedOptionsCardListBuilderId || '';
    if (!builderId) return null;

    return document.getElementById(builderId);
  }

  function syncSlider(root, itemCount) {
    const itemsRoot = root.querySelector(itemsSelector);
    const controls = root.querySelector(controlsSelector);
    const slider = itemsRoot?.closest('slider-component');
    const buttons = controls?.querySelectorAll('button') || [];
    const hasMultipleItems = itemCount > 1;

    if (itemsRoot) itemsRoot.scrollLeft = 0;
    if (controls) controls.hidden = !hasMultipleItems;
    buttons.forEach((button) => {
      button.disabled = !hasMultipleItems || button.name === 'previous';
    });

    const currentPage = controls?.querySelector('.slider-counter--current');
    const totalPages = controls?.querySelector('.slider-counter--total');
    if (currentPage) currentPage.textContent = '1';
    if (totalPages) totalPages.textContent = String(itemCount);

    if (itemCount > 0 && typeof slider?.resetPages === 'function') slider.resetPages();
  }

  function renderList(root, options = {}) {
    const itemsRoot = root.querySelector(itemsSelector);
    const builder = getBuilder(root);
    const snapshot = options.snapshot || (typeof builder?.getSelectedOptionsSnapshot === 'function'
      ? builder.getSelectedOptionsSnapshot()
      : null);
    const selectedOptions = getSelectedOptions(snapshot);
    const imageSource = root.dataset.selectedOptionsImageSource === 'thumbnail' ? 'thumbnail' : 'preview';

    if (!itemsRoot || !snapshot || selectedOptions.length === 0) {
      itemsRoot?.replaceChildren();
      syncSlider(root, 0);
      root.hidden = true;
      return;
    }

    itemsRoot.replaceChildren(
      ...selectedOptions.map((option, index) => createOptionCard(
        option,
        imageSource,
        `Slide-${root.dataset.selectedOptionsCardListBuilderId}-${index + 1}`
      ))
    );
    itemsRoot.hidden = false;
    root.hidden = false;
    syncSlider(root, selectedOptions.length);

    if (!root.__selectedOptionsCommittedSelections) setCommittedSelections(root, snapshot);
    if (options.animateSaved) animateSavedCards(root, options.changedGroupKeys);
  }

  function renderAll() {
    document.querySelectorAll(rootSelector).forEach(renderList);
  }

  function renderMatchingBuilder(builder) {
    if (!builder?.id) return;

    document.querySelectorAll(rootSelector).forEach((root) => {
      if (root.dataset.selectedOptionsCardListBuilderId === builder.id) renderList(root);
    });
  }

  document.addEventListener('product-builder:selection-change', (event) => {
    renderMatchingBuilder(event.target);
  });

  document.addEventListener('product-builder:configuration-saved', (event) => {
    const builder = event.target;
    if (!builder?.id) return;

    document.querySelectorAll(rootSelector).forEach((root) => {
      if (root.dataset.selectedOptionsCardListBuilderId !== builder.id) return;

      const snapshot = event.detail?.snapshot || null;
      const changedGroupKeys = getChangedGroupKeys(root.__selectedOptionsCommittedSelections, snapshot?.selections || {});
      renderList(root, {
        snapshot,
        animateSaved: true,
        changedGroupKeys,
      });
      setCommittedSelections(root, snapshot);
    });
  });

  if (window.customElements && typeof window.customElements.whenDefined === 'function') {
    window.customElements.whenDefined('express-product-builder').then(renderAll).catch(() => undefined);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', renderAll);
  } else {
    renderAll();
  }
})();
